import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Input, InputNumber, Segmented, Select, Skeleton, Table, Tooltip } from "antd";
import { CaretDownOutlined, CaretRightOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { MaterialCivilDay, MaterialLine, MaterialSheet } from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { fmtQty } from "../executionNorm";
import { amountOf, fmtPerUnit, fromInput, inputValue, isPresent, perUnitLabel, perUnitOf } from "./daySheet";
import {
  LineDraft,
  MaterialCategoryGroup,
  TaskLines,
  actualAmountOf,
  actualTillDateAmountOf,
  issuedTillDateOf,
  groupByCategory,
  issuedTotal,
  lineKey,
  pendingDaysTotal,
  sheetTotals,
  targetAmountOf,
  variancePct,
} from "./materialCategories";

// One opened task on the Material-Stores sheet, shown in the task drawer: its
// material lines in ONE table, grouped by CATEGORY under collapsible header rows
// that carry the group's target and actual subtotal — the shape the client's
// own cost analysis is read in (Explosives · Rocktools · … , ₹ per group).
// Filters (search, category, fill status) only narrow what is SHOWN; the save
// always sends every line of the task.
//
// TARGET is derived (norm × produced, at the norm's frozen rate) and read-only.
// ACTUAL is the clerk's: issued qty, the rate they record, and the remarks.
// The Actual Rate is never prefilled from the norm or the suggestion — a
// defaulted figure would be saved as if someone had checked it.

const MUTED = "#8094ae";
const INK = "#364a63";
const READ_ONLY = "#526484";

/** A ₹ figure, or an em dash. Quantities keep 4 dp (fmtQty); money is ₹ x. */
const money = (value: number | null, strong = false) => (
  <span
    className="exec-num"
    style={{ color: value === null ? MUTED : INK, fontWeight: value === null || !strong ? 400 : 600 }}
  >
    {value === null ? "—" : `₹ ${fmtQty(value)}`}
  </span>
);

const Variance: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null) return null;
  // Over the plan reads red, under it stays quiet — a shortfall on a capture
  // screen is information, not an error.
  const over = pct > 0.5;
  return (
    <Tooltip title="Actual against the day's target">
      <span className={`pms-exec-delta${over ? " pms-exec-hint--red" : ""}`}>
        {pct > 0 ? "+" : ""}
        {pct.toFixed(1)}%
      </span>
    </Tooltip>
  );
};

export type LineStatusFilter = "all" | "toFill" | "entered" | "pending";
export type LinesView = "both" | "actual";

const EPS = 0.00005;
const dayLabel = (workDate: string) => dayjs(workDate).format("DD-MMM-YYYY");

// A civil day's figures as the date-wise view uses them. "Room" is what
// today's issue may book on the day: its requirement less what EARLIER issues
// already booked (today's own booking is the editable part).
const num = (v: string) => (v !== "" && Number.isFinite(Number(v)) ? Number(v) : 0);
export const bookedEarlierOf = (day: MaterialCivilDay) => Math.max(0, num(day.bookedQty) - num(day.bookedTodayQty));
export const roomOf = (day: MaterialCivilDay) => Math.max(0, num(day.requiredQty) - bookedEarlierOf(day));
const q4 = (n: number) => String(Math.round(n * 10000) / 10000);
/** A day the date-wise view lists: still owed, or booked by today's issue. */
const isLiveDay = (day: MaterialCivilDay) => num(day.pendingQty) > 0 || num(day.bookedTodayQty) > 0;

/**
 * Lines with a civil day not yet booked in full by today's draft — only
 * `onDate` when the drawer was opened from that civil date.
 */
export const pendingLinesOf = (entry: TaskLines | undefined, onDate?: string | null): MaterialLine[] =>
  entry?.sheet
    ? entry.sheet.lines.filter((line) => {
        const days = entry.draft[lineKey(line)]?.pendingDays ?? {};
        return line.civilDays.some(
          (day) => (!onDate || day.workDate === onDate) && num(days[day.workDate] ?? "") + EPS < roomOf(day)
        );
      })
    : [];

/** Every open day (or `onDate` only) booked in full, for "Fill pending". */
export const owedFor = (line: MaterialLine, onDate?: string | null): Record<string, string> =>
  Object.fromEntries(
    line.civilDays
      .filter((day) => (!onDate || day.workDate === onDate) && roomOf(day) > EPS)
      .map((day) => [day.workDate, q4(roomOf(day))])
  );

type EditorRow =
  | { kind: "group"; key: string; group: MaterialCategoryGroup; shown: number; open: boolean }
  | { kind: "line"; key: string; line: MaterialLine; index: number }
  // The Pending view: civil dates as the group rows, a material owed to that
  // date under each.
  | { kind: "day"; key: string; workDate: string; producedQty: string; lines: MaterialLine[] }
  | { kind: "owed"; key: string; line: MaterialLine; day: MaterialCivilDay; index: number };

const MaterialLinesEditor: React.FC<{
  entry?: TaskLines;
  locked: boolean;
  uomOptions: Array<{ value: number; label: string }>;
  // The view and status filter are the caller's so they survive moving from
  // task to task in the drawer; search and categories reset per task.
  view: LinesView;
  onViewChange: (view: LinesView) => void;
  statusFilter: LineStatusFilter;
  onStatusFilterChange: (status: LineStatusFilter) => void;
  onChange: (key: string, patch: Partial<LineDraft>) => void;
  onRetry: () => void;
  // Opened from a civil date in the date-wise Pending list: the Pending view
  // shows that date only.
  focusDate?: string | null;
}> = ({ entry, locked, uomOptions, view, onViewChange, statusFilter, onStatusFilterChange, onChange, onRetry, focusDate }) => {
  const { scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  const [search, setSearch] = useState("");
  const [categoryKeys, setCategoryKeys] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  // Pending view: explicit open/closed per civil date; unset = open, or only
  // the focus date open when the drawer came from a date.
  const [dayOpen, setDayOpen] = useState<Record<string, boolean>>({});
  const dayIsOpen = (key: string, workDate: string) => dayOpen[key] ?? (!focusDate || workDate === focusDate);
  const tableRef = useRef<HTMLDivElement>(null);

  const draftFor = (line: MaterialLine) => entry?.draft[lineKey(line)];
  const groups = useMemo<MaterialCategoryGroup[]>(
    () => (entry?.sheet ? groupByCategory(entry.sheet.lines, draftFor) : []),
    // Regrouping on every draft keystroke is what keeps the subtotals live; the
    // line count here is single digits per task (5.5 on average).
    [entry?.sheet, entry?.draft] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const statusOf = (line: MaterialLine) => {
    const filled = isPresent(issuedTotal(draftFor(line)));
    // Owed to earlier civil dates — whether or not today's qty is typed. Opened
    // from one civil date, only what is owed to THAT date counts.
    // Booked days count too: they are listed so today's booking can be fixed.
    const pending = focusDate
      ? line.civilDays.some((day) => day.workDate === focusDate && num(day.requiredQty) > 0)
      : line.civilDays.some(isLiveDay);
    return { filled, pending };
  };
  const matchesStatus = (line: MaterialLine, status: LineStatusFilter) => {
    const { filled, pending } = statusOf(line);
    if (status === "toFill") return !filled;
    if (status === "entered") return filled;
    if (status === "pending") return pending;
    return true;
  };
  const allLines = entry?.sheet?.lines ?? [];
  const statusCounts = {
    all: allLines.length,
    toFill: allLines.filter((l) => matchesStatus(l, "toFill")).length,
    entered: allLines.filter((l) => matchesStatus(l, "entered")).length,
    pending: allLines.filter((l) => matchesStatus(l, "pending")).length,
  };

  const needle = search.trim().toLowerCase();
  const filtering = !!needle || categoryKeys.length > 0 || statusFilter !== "all";
  // A chip counts what it would SHOW under the current status and search —
  // never the category's whole size, which read as "4 here" over an empty list.
  const chipCount = (group: MaterialCategoryGroup) =>
    group.lines.filter(
      (line) => matchesStatus(line, statusFilter) && (!needle || line.materialName.toLowerCase().includes(needle))
    ).length;
  const byDate = statusFilter === "pending";
  const rows: EditorRow[] = [];
  let index = 0;
  if (byDate) {
    // Pending, date-wise: every civil date still owed (oldest first), with the
    // materials owed to it — search and category chips still narrow the lines.
    const visible = groups
      .filter((group) => !categoryKeys.length || categoryKeys.includes(group.key))
      .flatMap((group) => group.lines)
      .filter((line) => !needle || line.materialName.toLowerCase().includes(needle));
    const days = new Map<string, { producedQty: string; owed: Array<{ line: MaterialLine; day: MaterialCivilDay }> }>();
    // Opened from a civil date in the Pending list: that date only. From the
    // All civil-done list: every date still owed.
    visible.forEach((line) =>
      // Focused: every material the date requires (booked or not). Otherwise:
      // the days still owed or booked by today's issue.
      line.civilDays
        .filter((day) => (focusDate ? day.workDate === focusDate && num(day.requiredQty) > 0 : isLiveDay(day)))
        .forEach((day) => {
        const slot = days.get(day.workDate) ?? { producedQty: day.producedQty, owed: [] };
        slot.owed.push({ line, day });
        days.set(day.workDate, slot);
      })
    );
    Array.from(days.keys())
      .sort()
      .forEach((workDate) => {
        const slot = days.get(workDate)!;
        const dayKey = `D-${workDate}`;
        rows.push({ kind: "day", key: dayKey, workDate, producedQty: slot.producedQty, lines: slot.owed.map((o) => o.line) });
        if (!dayIsOpen(dayKey, workDate)) return;
        slot.owed.forEach(({ line, day }) =>
          rows.push({ kind: "owed", key: `${dayKey}-${lineKey(line)}`, line, day, index: index++ })
        );
      });
  } else groups.forEach((group) => {
    if (categoryKeys.length && !categoryKeys.includes(group.key)) return;
    const shown = group.lines.filter(
      (line) =>
        matchesStatus(line, statusFilter) && (!needle || line.materialName.toLowerCase().includes(needle))
    );
    if (!shown.length) return;
    // A search or filter always shows its hits, even in a collapsed group.
    const open = filtering || !collapsed.has(group.key);
    rows.push({ kind: "group", key: group.key, group, shown: shown.length, open });
    if (open) shown.forEach((line) => rows.push({ kind: "line", key: lineKey(line), line, index: index++ }));
  });

  if (!entry || (entry.loading && !entry.sheet)) {
    return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
  }
  if (entry.error && !entry.sheet) {
    return (
      <Alert
        type="error"
        showIcon
        message={entry.error}
        action={
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }
  const sheet = entry.sheet as MaterialSheet;
  if (!sheet.lines.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No material norms on this task" />;
  }

  // Measured against civil done TILL DATE — the entry is made after the work.
  const noCivil = sheet.producedTillDate === "";
  const disabled = locked || entry.saving;
  const showTarget = view === "both";

  const unit = perUnitLabel(sheet.workUomName);
  const totals = sheetTotals(groups, sheet.producedTillDate);
  const perUnit = (amount: number | null) => (amount === null ? null : perUnitOf(amount, sheet.producedTillDate));

  // A ₹ figure with its ₹ / unit underneath — the same pairing the Mechanical
  // sheet uses for its head blocks.
  const figure = (label: string, amount: number | null, withPerUnit = true) => {
    const v = withPerUnit ? perUnit(amount) : null;
    return (
      <span className="pms-exec-heads-figure">
        <span className="pms-exec-heads-subtotal">
          {label} {amount === null ? "—" : `₹ ${fmtQty(amount)}`}
        </span>
        {v !== null && (
          <span className="pms-exec-per-unit">
            ₹ {fmtPerUnit(v)} / {unit}
          </span>
        )}
      </span>
    );
  };

  const toggleGroup = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Enter in an Issued Qty moves to the next visible line's qty.
  const focusQty = (i: number) => {
    const input = tableRef.current?.querySelector<HTMLInputElement>(`[data-qty-idx="${i}"] input`);
    if (input) {
      input.focus();
      input.select();
    }
  };

  const widths = {
    material: w(260),
    norm: w(110),
    tRate: w(100),
    tQty: w(110),
    tAmount: w(120),
    tPerUnit: w(110),
    uom: w(130),
    tillDate: w(130),
    issued: w(160),
    rate: w(130),
    amount: w(130),
    perUnit: w(110),
    remarks: w(220),
  };
  const hidden: Array<keyof typeof widths> = showTarget
    ? []
    : ["norm", "tRate", "tQty", "tAmount", "tPerUnit", "perUnit"];
  if (!uomOptions.length) hidden.push("uom");
  const leafKeys = (Object.keys(widths) as Array<keyof typeof widths>).filter((key) => !hidden.includes(key));
  const scrollX = leafKeys.reduce((acc, key) => acc + widths[key], 0);

  // A category row is two cells: the name under Material, and its figures
  // spanning every other column.
  const spanCell = (row: EditorRow, leaf: string) => {
    if (row.kind !== "group") return {};
    if (leaf === "material") return { className: "pms-mat-group-cell" };
    if (leaf === leafKeys[1]) return { colSpan: leafKeys.length - 1, className: "pms-mat-group-cell" };
    return { colSpan: 0 };
  };
  const lineOnly =
    (leaf: string, render: (line: MaterialLine, row: EditorRow & { kind: "line" }) => React.ReactNode) =>
    (_: any, row: EditorRow) => {
      if (row.kind === "line") return render(row.line, row);
      if (row.kind !== "group" || leaf !== leafKeys[1]) return null;
      const groupVariance = variancePct(row.group.actualAmount, row.group.targetAmount);
      return (
        <span className="pms-exec-cat-figures pms-mat-group-figures">
          {showTarget && figure("target", row.group.targetAmount, false)}
          {figure("actual", row.group.actualAmount, false)}
          {showTarget && <Variance pct={groupVariance} />}
        </span>
      );
    };
  const leaf = (key: keyof typeof widths, column: Record<string, any>, render: Parameters<typeof lineOnly>[1]) => ({
    key,
    width: widths[key],
    ...column,
    onCell: (row: EditorRow) => spanCell(row, key),
    render: lineOnly(key, render),
  });

  const materialColumn = {
    title: "Material",
    key: "material",
    width: widths.material,
    fixed: "left" as const,
    onCell: (row: EditorRow) => spanCell(row, "material"),
    render: (_: any, row: EditorRow) => {
      if (row.kind === "group") {
        return (
          <span className="pms-mat-group-name" onClick={() => !filtering && toggleGroup(row.key)}>
            {!filtering && (row.open ? <CaretDownOutlined /> : <CaretRightOutlined />)}
            <span className="pms-eyebrow">{row.group.categoryName}</span>
            <span className="pms-exec-group-count">
              {row.shown === row.group.lineCount ? row.group.lineCount : `${row.shown} of ${row.group.lineCount}`}
            </span>
          </span>
        );
      }
      if (row.kind !== "line") return null;
      const line = row.line;
      return (
        <div style={{ paddingLeft: 18 }}>
          <div style={{ color: "#022c57", fontWeight: 600 }}>{line.materialName}</div>
          <div className="pms-exec-hint">
            allotted <span className="exec-num">{fmtQty(line.allottedQuantity)}</span> {line.uomName}
          </div>
        </div>
      );
    },
  };

  // ---------------------------------------------------- target (derived, read-only)
  const targetColumns = [
    leaf("norm", { title: `Norm / ${unit}`, align: "right" as const }, (line) => (
      <Tooltip title="Allotted per unit of the task's output — includes PUC factor and wastage. A material reaching the task through more than one norm shows the sum.">
        <span className="exec-num" style={{ color: line.normQtyPerUnit === "" ? MUTED : READ_ONLY }}>
          {line.normQtyPerUnit === "" ? "—" : fmtQty(line.normQtyPerUnit)}
        </span>
      </Tooltip>
    )),
    {
      title: "Target (till date)",
      key: "target",
      children: [
        leaf("tRate", { title: "Rate", align: "right" as const }, (line) => (
          <Tooltip title={line.normRate === "" ? "The norm lines carry no price, so there is no target amount" : "The rate frozen on the norm application"}>
            <span className="exec-num" style={{ color: line.normRate === "" ? MUTED : READ_ONLY }}>
              {line.normRate === "" ? "—" : fmtQty(line.normRate)}
            </span>
          </Tooltip>
        )),
        leaf("tQty", { title: "Qty", align: "right" as const }, (line) => (
          <span className="exec-num" style={{ color: line.targetQtyTillDate === "" ? MUTED : READ_ONLY }}>
            {line.targetQtyTillDate === "" ? "—" : fmtQty(line.targetQtyTillDate)}
          </span>
        )),
        leaf("tAmount", { title: "Amount", align: "right" as const }, (line) => money(targetAmountOf(line))),
        leaf("tPerUnit", { title: `₹ / ${unit}`, align: "right" as const }, (line) => {
          const v = perUnit(targetAmountOf(line));
          return <span className="exec-num" style={{ color: MUTED }}>{v === null ? "—" : fmtPerUnit(v)}</span>;
        }),
      ],
    },
  ];

  // ---------------------------------------------------- actual (what the clerk records)
  const actualChildren = [
    ...(uomOptions.length
      ? [
          leaf("uom", { title: "UOM" }, (line) => {
            const draft = draftFor(line);
            return (
              <Select
                size="small"
                value={draft?.uomId || undefined}
                onChange={(v) => onChange(lineKey(line), { uomId: Number(v) })}
                options={uomOptions}
                showSearch
                optionFilterProp="label"
                placeholder={line.uomName || "UOM"}
                disabled={disabled}
                style={{ width: "100%" }}
              />
            );
          }),
        ]
      : []),
    leaf("tillDate", { title: "Issued till date", align: "right" as const }, (line) => {
      const v = issuedTillDateOf(line, draftFor(line));
      const over = v !== null && line.targetQtyTillDate !== "" && v > Number(line.targetQtyTillDate) + 0.00005;
      return (
        <Tooltip title="Every issue up to today, with what you are typing today in place of today's saved figure">
          <span className="exec-num" style={{ color: v === null ? MUTED : over ? "#cf1322" : INK }}>
            {v === null ? "—" : fmtQty(String(v))}
          </span>
        </Tooltip>
      );
    }),
    leaf("issued", { title: "Issued today" }, (line, row) => {
      const draft = draftFor(line);
      const qty = draft?.qty ?? "";
      const tillDate = issuedTillDateOf(line, draft);
      const overTarget =
        isPresent(qty) && tillDate !== null && line.targetQtyTillDate !== "" && tillDate > Number(line.targetQtyTillDate) + 0.00005;
      return (
        <div data-qty-idx={row.index}>
          <InputNumber
            stringMode
            min="0"
            step="0.01"
            placeholder="0.00"
            className={overTarget ? "pms-exec-num-over" : undefined}
            value={inputValue(qty)}
            onChange={(v) => onChange(lineKey(line), { qty: fromInput(v) })}
            onPressEnter={() => focusQty(row.index + 1)}
            disabled={disabled}
          />
          {!showTarget && line.targetQtyTillDate !== "" && (
            <div className="pms-exec-hint">
              target till date <span className="exec-num">{fmtQty(line.targetQtyTillDate)}</span> {line.uomName}
            </div>
          )}
          {pendingDaysTotal(draft) > 0 ? (
            // Booked against earlier civil dates in the Pending view; saved
            // with today's qty as one issue.
            <div className="pms-exec-hint">
              + <span className="exec-num">{fmtQty(String(pendingDaysTotal(draft)))}</span> earlier civil → issue{" "}
              <span className="exec-num" style={{ color: INK, fontWeight: 600 }}>{fmtQty(issuedTotal(draft))}</span>
            </div>
          ) : (
            line.pendingByDate.length > 0 &&
            !disabled && (
              // Owed to civil work already done (often a day late). Offered,
              // never prefilled: a defaulted qty would save as if checked.
              <div className="pms-exec-hint">
                owed {fmtQty(line.pendingQty)} to {line.pendingByDate.length} civil date
                {line.pendingByDate.length === 1 ? "" : "s"}{" "}
                <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => onStatusFilterChange("pending")}>
                  book by date
                </Button>
              </div>
            )
          )}
          {isPresent(qty) && noCivil && (
            <div className="pms-exec-hint pms-exec-hint--amber">no civil work yet</div>
          )}
          {overTarget && <div className="pms-exec-hint pms-exec-hint--amber">over target till date</div>}
        </div>
      );
    }),
    leaf("rate", { title: "Actual Rate" }, (line) => {
      const draft = draftFor(line);
      return (
        <InputNumber
          stringMode
          min="0"
          step="0.01"
          placeholder="0.00"
          value={inputValue(draft?.rate ?? "")}
          onChange={(v) => onChange(lineKey(line), { rate: fromInput(v), rateSource: "ENTERED" })}
          disabled={disabled}
        />
      );
    }),
    // Issued today × Actual Rate, live as either is typed.
    leaf("amount", { title: "Amount today", align: "right" as const }, (line) => money(actualAmountOf(draftFor(line)), true)),
    ...(showTarget
      ? [
          // Actual money till date over civil done till date — beside the
          // target's ₹ / unit on the same basis.
          leaf("perUnit", { title: `₹ / ${unit} (till date)`, align: "right" as const }, (line) => {
            const v = perUnit(actualTillDateAmountOf(line, draftFor(line)));
            return (
              <span className="exec-num" style={{ color: v === null ? MUTED : READ_ONLY }}>
                {v === null ? "—" : fmtPerUnit(v)}
              </span>
            );
          }),
        ]
      : []),
  ];
  const actualColumns = showTarget ? [{ title: "Actual (recorded)", key: "actual", children: actualChildren }] : actualChildren;

  const remarksColumn = leaf("remarks", { title: "Remarks" }, (line) => {
    const draft = draftFor(line);
    return (
      <Input
        size="small"
        value={draft?.remarks ?? ""}
        onChange={(e) => onChange(lineKey(line), { remarks: e.target.value })}
        maxLength={200}
        placeholder="—"
        disabled={disabled}
      />
    );
  });

  const columns = showTarget
    ? [materialColumn, ...targetColumns, ...actualColumns, remarksColumn]
    : [materialColumn, ...actualColumns, remarksColumn];

  // ---------------------------------------------------- pending, date-wise
  // Civil days oldest first, booked ones included. Target is the norm for the
  // day's output. The Actual Qty box holds the day's whole booking: what
  // earlier days' issues booked there plus this issue's share, which is saved
  // against that civil date and booked on it exactly (over target included).
  const daysOf = (line: MaterialLine) => draftFor(line)?.pendingDays ?? {};
  const setDay = (line: MaterialLine, workDate: string, qty: string) =>
    onChange(lineKey(line), { pendingDays: { ...daysOf(line), [workDate]: qty } });
  const dayOf = (line: MaterialLine, workDate: string) => line.civilDays.find((day) => day.workDate === workDate);
  // Money booked on a date by EARLIER days' issues: the day's booked amount
  // less today's saved share at today's saved rate (today's share is shown live
  // from the draft instead). rate = amount / priced qty; null when unpriced.
  const earlierMoneyOf = (line: MaterialLine, day: MaterialCivilDay) => {
    if (day.bookedAmount === "") return { amount: null as number | null, rate: null as number | null };
    const savedShare = num(day.bookedTodayQty);
    const savedRate = isPresent(line.rate) ? Number(line.rate) : null;
    const amount = num(day.bookedAmount) - (savedRate === null ? 0 : savedShare * savedRate);
    const priced = num(day.bookedPricedQty) - (savedRate === null ? 0 : savedShare);
    if (priced <= EPS) return { amount: null, rate: null };
    return { amount: Math.round(amount * 100) / 100, rate: amount / priced };
  };
  const owedAfter = (line: MaterialLine, day: MaterialCivilDay) =>
    Math.max(0, roomOf(day) - num(daysOf(line)[day.workDate] ?? ""));
  const pendingWidths = {
    material: w(300),
    required: w(130),
    tRate: w(110),
    tAmount: w(120),
    book: w(190),
    rate: w(130),
    amount: w(120),
  };
  const pendingScrollX = Object.values(pendingWidths).reduce((acc, n) => acc + n, 0);
  const daySpan = (row: EditorRow, key: string) => {
    if (row.kind !== "day") return {};
    if (key === "material") return { className: "pms-mat-group-cell" };
    if (key === "required") return { colSpan: 6, className: "pms-mat-group-cell" };
    return { colSpan: 0 };
  };
  const qtyCell = (value: number, line: MaterialLine, strong = false) => (
    <span className="exec-num" style={{ color: value > EPS ? (strong ? INK : READ_ONLY) : MUTED, fontWeight: strong ? 600 : 400 }}>
      {value > EPS ? fmtQty(q4(value)) : "—"} {value > EPS && <span style={{ color: MUTED, fontWeight: 400 }}>{line.uomName}</span>}
    </span>
  );
  const pendingColumns = [
    {
      title: "Civil date / Material",
      key: "material",
      width: pendingWidths.material,
      fixed: "left" as const,
      onCell: (row: EditorRow) => daySpan(row, "material"),
      render: (_: any, row: EditorRow) => {
        if (row.kind === "day") {
          const open = dayIsOpen(row.key, row.workDate);
          return (
            <span className="pms-mat-group-name" onClick={() => setDayOpen((current) => ({ ...current, [row.key]: !open }))}>
              {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
              <span style={{ color: "#022c57", fontWeight: 600 }}>{dayLabel(row.workDate)}</span>
              {row.producedQty !== "" && (
                <span className="pms-exec-group-count">
                  produced {fmtQty(row.producedQty)} {sheet.workUomName}
                </span>
              )}
            </span>
          );
        }
        if (row.kind !== "owed") return null;
        return (
          <div style={{ paddingLeft: 18 }}>
            <div style={{ color: "#022c57", fontWeight: 600 }}>{row.line.materialName}</div>
            <div className="pms-exec-hint">{row.line.categoryName || "Uncategorized"}</div>
          </div>
        );
      },
    },
    {
      // Norm per unit of output × that day's civil output.
      title: "Target",
      key: "required",
      width: pendingWidths.required,
      align: "right" as const,
      onCell: (row: EditorRow) => daySpan(row, "required"),
      render: (_: any, row: EditorRow) => {
        if (row.kind === "day") {
          const owing = row.lines.filter((line) => {
            const day = dayOf(line, row.workDate);
            return !!day && owedAfter(line, day) > EPS;
          });
          const bookedToday = row.lines.filter((line) => num(daysOf(line)[row.workDate] ?? "") > EPS);
          return (
            <span className="pms-mat-day-actions">
              <span className="pms-exec-hint">
                {row.lines.length} material{row.lines.length === 1 ? "" : "s"} · {owing.length} owed ·{" "}
                {bookedToday.length} booked today
              </span>
              {!disabled && owing.length > 0 && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    owing.forEach((line) => {
                      const day = dayOf(line, row.workDate);
                      if (day) setDay(line, row.workDate, q4(roomOf(day)));
                    });
                  }}
                >
                  Fill this date
                </Button>
              )}
              {!disabled && bookedToday.length > 0 && (
                <Tooltip title="Remove today's booking on this date (saved bookings are reduced when you save)">
                  <Button
                    size="small"
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      row.lines.forEach((line) => setDay(line, row.workDate, ""));
                    }}
                  >
                    Clear today
                  </Button>
                </Tooltip>
              )}
            </span>
          );
        }
        if (row.kind !== "owed") return null;
        return qtyCell(num(row.day.requiredQty), row.line);
      },
    },
    {
      title: "Target Rate",
      key: "tRate",
      width: pendingWidths.tRate,
      align: "right" as const,
      onCell: (row: EditorRow) => daySpan(row, "tRate"),
      render: (_: any, row: EditorRow) =>
        row.kind === "owed" ? (
          <Tooltip title={row.line.normRate === "" ? "The norm carries no price" : "The rate frozen on the norm"}>
            <span className="exec-num" style={{ color: row.line.normRate === "" ? MUTED : READ_ONLY }}>
              {row.line.normRate === "" ? "—" : fmtQty(row.line.normRate)}
            </span>
          </Tooltip>
        ) : null,
    },
    {
      title: "Target Amount",
      key: "tAmount",
      width: pendingWidths.tAmount,
      align: "right" as const,
      onCell: (row: EditorRow) => daySpan(row, "tAmount"),
      render: (_: any, row: EditorRow) => {
        if (row.kind !== "owed") return null;
        const amount = amountOf(row.day.requiredQty, row.line.normRate);
        return money(amount === "" ? null : Number(amount));
      },
    },
    {
      // The box holds the date's WHOLE booking, as it would be typed: what
      // earlier days' issues booked plus today's share. Only today's share is
      // saved with today's issue, so the box never goes below the earlier part.
      title: "Actual Qty",
      key: "book",
      width: pendingWidths.book,
      onCell: (row: EditorRow) => daySpan(row, "book"),
      render: (_: any, row: EditorRow) => {
        if (row.kind !== "owed") return null;
        const { line, day } = row;
        const todayValue = daysOf(line)[day.workDate] ?? "";
        const earlier = bookedEarlierOf(day);
        const required = num(day.requiredQty);
        const shown = isPresent(todayValue) ? q4(earlier + num(todayValue)) : earlier > EPS ? q4(earlier) : "";
        const room = roomOf(day);
        // Fully booked by earlier days: shown, not editable from today's entry.
        const fixed = room <= EPS && num(day.bookedTodayQty) <= EPS;
        const setTotal = (total: string) => {
          if (total === "") return setDay(line, day.workDate, "");
          const share = num(total) - earlier;
          setDay(line, day.workDate, share > EPS ? q4(share) : "");
        };
        return (
          <div data-qty-idx={row.index}>
            <Tooltip
              title={
                fixed
                  ? "Fully booked by an earlier day's issue"
                  : earlier > EPS
                    ? `${fmtQty(q4(earlier))} of this was booked by an earlier day's issue`
                    : ""
              }
            >
              <InputNumber
                stringMode
                min={q4(earlier)}
                step="0.01"
                placeholder="0.00"
                value={inputValue(shown)}
                onChange={(v) => setTotal(fromInput(v))}
                onPressEnter={() => focusQty(row.index + 1)}
                disabled={disabled || fixed}
              />
            </Tooltip>
            {!fixed && room > EPS && num(todayValue) + EPS < room && !disabled && (
              <div className="pms-exec-hint">
                <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => setDay(line, day.workDate, q4(room))}>
                  use {fmtQty(q4(required))}
                </Button>
              </div>
            )}
            {num(shown) > required + EPS && (
              // Booked on this date as entered — never moved to another day.
              <div className="pms-exec-hint pms-exec-hint--amber">
                over target by {fmtQty(q4(num(shown) - required))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Actual Rate",
      key: "rate",
      width: pendingWidths.rate,
      onCell: (row: EditorRow) => daySpan(row, "rate"),
      render: (_: any, row: EditorRow) => {
        if (row.kind !== "owed") return null;
        const earlier = earlierMoneyOf(row.line, row.day);
        const todayShare = num(daysOf(row.line)[row.day.workDate] ?? "");
        const earlierRate =
          earlier.rate === null ? null : (
            <Tooltip title="Rate of the earlier issues booked on this date">
              <span className="exec-num" style={{ color: READ_ONLY }}>{fmtQty(q4(earlier.rate))}</span>
            </Tooltip>
          );
        // Nothing typed for today on this date: show what is booked — the
        // earlier issues' rate — and no empty input beside a booked quantity.
        if (todayShare <= EPS && (earlier.rate !== null || (roomOf(row.day) <= EPS && num(row.day.bookedTodayQty) <= EPS))) {
          return earlierRate;
        }
        const draft = draftFor(row.line);
        // Today's share is being entered: today's rate (one per material for
        // the whole issue), with the earlier rate underneath when there is one.
        return (
          <div>
            <Tooltip title="One rate per material for today's whole issue">
              <InputNumber
                stringMode
                min="0"
                step="0.01"
                placeholder="0.00"
                value={inputValue(draft?.rate ?? "")}
                onChange={(v) => onChange(lineKey(row.line), { rate: fromInput(v), rateSource: "ENTERED" })}
                disabled={disabled}
              />
            </Tooltip>
            {earlier.rate !== null && (
              <div className="pms-exec-hint">earlier @ {fmtQty(q4(earlier.rate))}</div>
            )}
          </div>
        );
      },
    },
    {
      // The date's whole booking in money: earlier issues at their own rates
      // plus today's share at today's rate.
      title: "Actual Amount",
      key: "amount",
      width: pendingWidths.amount,
      align: "right" as const,
      onCell: (row: EditorRow) => daySpan(row, "amount"),
      render: (_: any, row: EditorRow) => {
        if (row.kind !== "owed") return null;
        const earlier = earlierMoneyOf(row.line, row.day).amount;
        const today = amountOf(daysOf(row.line)[row.day.workDate] ?? "", draftFor(row.line)?.rate ?? "");
        if (earlier === null && today === "") return money(null);
        return money(Math.round(((earlier ?? 0) + num(today)) * 100) / 100);
      },
    },
  ];

  const toggleCategory = (key: string) =>
    setCategoryKeys((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
  const clearFilters = () => {
    setSearch("");
    setCategoryKeys([]);
    onStatusFilterChange("all");
  };

  return (
    <div className="pms-exec-lines pms-mat-editor">
      <div className="pms-exec-lines-head">
        <span className="pms-exec-hint">
          {sheet.lines.length} material{sheet.lines.length === 1 ? "" : "s"} in {groups.length} categor
          {groups.length === 1 ? "y" : "ies"} ·{" "}
          {noCivil ? (
            <span className="pms-exec-hint--amber">no civil work on this task yet</span>
          ) : (
            <>
              civil done till date <span className="exec-num">{fmtQty(sheet.producedTillDate)}</span> {sheet.workUomName}
            </>
          )}
          {sheet.hasEntryForDate ? " · issue recorded" : " · no issue recorded yet"}
        </span>
        <span className="pms-exec-cat-figures">
          {figure("target till date", totals.targetAmount)}
          {figure("actual till date", totals.actualAmount)}
          <Variance pct={totals.variancePct} />
        </span>
      </div>

      <div className="pms-mat-filters">
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined style={{ color: MUTED }} />}
          placeholder="Search material"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: w(220) }}
        />
        <Segmented
          size="small"
          value={statusFilter}
          onChange={(v) => onStatusFilterChange(v as LineStatusFilter)}
          options={[
            { label: `All ${statusCounts.all}`, value: "all" },
            { label: `To fill ${statusCounts.toFill}`, value: "toFill" },
            { label: `Entered ${statusCounts.entered}`, value: "entered" },
            ...(statusCounts.pending ? [{ label: `By civil date ${statusCounts.pending}`, value: "pending" }] : []),
          ]}
        />
        <Segmented
          size="small"
          value={view}
          onChange={(v) => onViewChange(v as LinesView)}
          options={[
            { label: "Actual only", value: "actual" },
            { label: "Target + actual", value: "both" },
          ]}
          style={{ marginLeft: "auto" }}
        />
      </div>
      {groups.length > 1 && (
        <div className="pms-mat-chips">
          {groups.map((group) => {
            const count = chipCount(group);
            const on = categoryKeys.includes(group.key);
            return (
              <button
                type="button"
                key={group.key}
                className={`pms-mat-chip${on ? " pms-mat-chip--on" : ""}${count === 0 && !on ? " pms-mat-chip--empty" : ""}`}
                disabled={count === 0 && !on}
                onClick={() => toggleCategory(group.key)}
              >
                {group.categoryName} <span className="pms-mat-chip-count">{count}</span>
              </button>
            );
          })}
          {filtering && (
            <Button type="link" size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {byDate && focusDate && (
        <div className="pms-exec-hint" style={{ marginBottom: 6 }}>
          Showing what is owed for <b style={{ color: INK }}>{dayLabel(focusDate)}</b> only
        </div>
      )}
      <div ref={tableRef}>
        <Table
          size="small"
          className="pms-exec-heads-table pms-mat-table"
          rowKey="key"
          dataSource={rows}
          columns={(byDate ? pendingColumns : columns) as any}
          pagination={false}
          rowClassName={(row: EditorRow) => (row.kind === "group" || row.kind === "day" ? "pms-mat-group-row" : "")}
          scroll={{ x: byDate ? pendingScrollX : scrollX, y: "calc(100vh - 360px)" }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  byDate
                    ? focusDate
                      ? `Nothing owed for ${dayLabel(focusDate)} in this selection`
                      : "Nothing owed to earlier civil work"
                    : "No materials match the filters"
                }
              >
                <Button size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              </Empty>
            ),
          }}
        />
      </div>
      <div className="pms-exec-hint" style={{ marginTop: 8 }}>
        Blank = not issued. Target is the norm for what was produced, at the rate frozen on the norm; cost per{" "}
        {unit} divides by the ACTUAL output. Quantities entered against a civil date are booked on that date as
        entered. Enter moves to the next quantity.
      </div>
    </div>
  );
};

export default MaterialLinesEditor;
